import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Plus, Pencil, Trash2, Upload, ArrowLeft, Eye, FileUp } from "lucide-react";
import { toast } from "sonner";
import { fmtKES, photoUrl } from "@/lib/format";
import BulkImportDialog from "@/components/BulkImportDialog";

const EMPTY = {
  name: "", country: "Kenya", category: "C", rating: 4.0,
  is_international: false, seed_level: 5, achievements: "",
  bio: "", registration_fee_paid: false,
  photo_url: "",
  current_ranking: "", fip_ranking: "",
};

const uploadPhoto = async (playerId, file) => {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post(`/players/${playerId}/photo`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data.photo_path;
};

export default function PlayersPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [players, setPlayers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [photoFile, setPhotoFile] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const canManage = ["super_admin", "league_admin"].includes(user.role);

  const load = async () => {
    const { data } = await api.get("/players");
    setPlayers(data);
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing({ new: true }); setForm(EMPTY); setPhotoFile(null); };
  const startEdit = (p) => { setEditing(p); setForm({ ...EMPTY, ...p }); setPhotoFile(null); };
  const cancel = () => { setEditing(null); setForm(EMPTY); setPhotoFile(null); };

  // Numeric fields on PlayerBody that React inputs may emit as "" when blank.
  // Strip them to null on save so Pydantic Optional[int] accepts them.
  const NUMERIC_FIELDS = ["seed_level", "minimum_bid", "original_ranking", "current_ranking", "fip_ranking"];
  const buildPayload = (f) => {
    const out = { ...f };
    for (const k of NUMERIC_FIELDS) {
      if (out[k] === "" || out[k] === undefined) out[k] = null;
    }
    return out;
  };

  const save = async () => {
    try {
      const payload = buildPayload(form);
      let playerId;
      if (editing.new) {
        const { data } = await api.post("/players", payload);
        playerId = data.id;
        toast.success("Player added");
      } else {
        await api.put(`/players/${editing.id}`, payload);
        playerId = editing.id;
        toast.success("Updated");
      }
      // Upload photo AFTER we have a player id (backend needs the id in the URL)
      if (photoFile && playerId) {
        try {
          await uploadPhoto(playerId, photoFile);
          toast.success("Photo uploaded");
        } catch (e) {
          toast.error(`Photo upload failed: ${formatApiError(e)}`);
        }
      }
      cancel();
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const upload = async (id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/players/${id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploaded");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Remove player?")) return;
    await api.delete(`/players/${id}`);
    load();
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Roster Manager</div>
          <h1 className="font-anton text-5xl md:text-6xl uppercase tracking-tighter text-white">Players</h1>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <Button data-testid="btn-bulk-import" onClick={() => setBulkOpen(true)} variant="outline" className="border-white/20 text-white bg-[#060d09] font-bold">
              <FileUp className="w-4 h-4 mr-1.5" /> Bulk Import
            </Button>
            <Button data-testid="btn-add-player" onClick={startNew} className="bg-[#00A86B] hover:bg-[#00935A] text-white font-bold">
              <Plus className="w-4 h-4 mr-1.5" /> Add Player
            </Button>
          </div>
        )}
      </div>

      <BulkImportDialog open={bulkOpen} onOpenChange={setBulkOpen} onImported={load} />

      {editing && (
        <Card className="p-6 bg-[#11241b] border-white/10 mb-6" data-testid="player-form">
          <div className="flex items-center justify-between mb-4">
            <div className="font-anton text-xl uppercase text-white">{editing.new ? "New Player" : `Edit ${editing.name}`}</div>
            <Button size="sm" variant="ghost" onClick={cancel} className="text-zinc-400"><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Full Name">
              <Input data-testid="player-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#060d09] border-white/10 text-white" />
            </Field>
            <Field label="Country">
              <Input data-testid="player-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="bg-[#060d09] border-white/10 text-white" />
            </Field>
            <Field label="Category">
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="player-category" className="bg-[#060d09] border-white/10 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#11241b] border-white/10 text-white">
                  {["A+", "A", "B", "C", "D", "E"].map((c) => <SelectItem key={c} value={c}>Category {c}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Rating">
              <Input data-testid="player-rating" type="number" step="0.1" value={form.rating} onChange={(e) => setForm({ ...form, rating: parseFloat(e.target.value) || 0 })} className="bg-[#060d09] border-white/10 text-white" />
            </Field>
            <Field label="Seed Level">
              <Input data-testid="player-seed" type="number" value={form.seed_level} onChange={(e) => setForm({ ...form, seed_level: parseInt(e.target.value) || 0 })} className="bg-[#060d09] border-white/10 text-white" />
            </Field>
            <Field label="Kenyan Ranking (current)">
              <Input
                data-testid="player-current-ranking"
                type="number"
                placeholder="e.g. 12"
                value={form.current_ranking ?? ""}
                onChange={(e) => setForm({ ...form, current_ranking: e.target.value === "" ? null : parseInt(e.target.value, 10) })}
                className="bg-[#060d09] border-white/10 text-white"
              />
              <div className="text-[10px] text-zinc-500 mt-1">Domestic Kenyan rank used for team-ranking totals & seed maths.</div>
            </Field>
            <Field label="FIP Ranking (International)">
              <Input
                data-testid="player-fip-ranking"
                type="number"
                placeholder="Leave blank if unranked"
                value={form.fip_ranking ?? ""}
                onChange={(e) => setForm({ ...form, fip_ranking: e.target.value === "" ? null : parseInt(e.target.value, 10) })}
                className="bg-[#060d09] border-white/10 text-white"
              />
              <div className="text-[10px] text-zinc-500 mt-1">Federación Internacional de Pádel rank — kept alongside domestic ranking.</div>
            </Field>
            <div className="flex items-end gap-6">
              <div className="flex items-center gap-2">
                <Switch data-testid="player-international" checked={form.is_international} onCheckedChange={(v) => setForm({ ...form, is_international: v })} />
                <Label className="text-zinc-300">International</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch data-testid="player-fee-paid" checked={form.registration_fee_paid} onCheckedChange={(v) => setForm({ ...form, registration_fee_paid: v })} />
                <Label className="text-zinc-300">Registration Fee Paid</Label>
              </div>
            </div>
            <Field label="Achievements" full>
              <Textarea data-testid="player-achievements" rows={4} value={form.achievements} onChange={(e) => setForm({ ...form, achievements: e.target.value })} className="bg-[#060d09] border-white/10 text-white" />
            </Field>
            <Field label="Short Bio" full>
              <Textarea data-testid="player-bio" rows={2} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="bg-[#060d09] border-white/10 text-white" />
            </Field>
            {/* --- PHOTO --- */}
            <Field label="Player Photo (upload)" full>
              <div className="flex items-center gap-3">
                {(photoFile || form.photo_path) && (
                  <div className="w-16 h-16 rounded-lg bg-black/40 border border-white/10 overflow-hidden shrink-0">
                    <img
                      src={photoFile ? URL.createObjectURL(photoFile) : photoUrl(form.photo_path)}
                      alt="preview"
                      className="w-full h-full object-cover"
                      data-testid="player-photo-preview"
                    />
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setPhotoFile(e.target.files?.[0] || null)}
                  data-testid="player-photo-input"
                  className="text-xs text-zinc-300 file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-[#F4B400] file:text-black file:font-bold file:cursor-pointer"
                />
                {photoFile && (
                  <button
                    type="button"
                    onClick={() => setPhotoFile(null)}
                    className="text-xs text-red-400 hover:text-red-300 underline"
                  >
                    Clear
                  </button>
                )}
              </div>
              <div className="text-[10px] text-zinc-500 mt-1">
                Optional. PNG / JPG / WebP · max ~5MB. Uploaded after the Save action.
              </div>
            </Field>
            <Field label="Or paste an external photo URL" full>
              <Input
                data-testid="player-photo-url"
                type="url"
                placeholder="https://... (Google Drive / any public image URL)"
                value={form.photo_url || ""}
                onChange={(e) => setForm({ ...form, photo_url: e.target.value })}
                className="bg-[#060d09] border-white/10 text-white"
              />
              <div className="text-[10px] text-zinc-500 mt-1">
                Use this if the player photo is already hosted elsewhere. If both are provided, the uploaded file wins.
              </div>
            </Field>
          </div>
          <div className="mt-6 flex gap-2">
            <Button data-testid="btn-save-player" onClick={save} className="bg-[#00A86B] hover:bg-[#00935A] text-white font-bold">Save</Button>
            <Button onClick={cancel} variant="outline" className="border-white/20 text-white">Cancel</Button>
          </div>
        </Card>
      )}

      <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {players.map((p) => (
          <Card key={p.id} className="overflow-hidden bg-[#11241b] border-white/10 group" data-testid={`player-card-${p.id}`}>
            <div className="aspect-[4/5] relative bg-[#060d09]">
              {p.photo_path ? (
                <img src={photoUrl(p.photo_path)} alt={p.name} className="absolute inset-0 w-full h-full object-cover" />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center font-anton text-7xl text-zinc-800">{p.name?.[0] || "?"}</div>
              )}
              <div className="absolute top-2 left-2 flex flex-col gap-1">
                <span className="text-[10px] px-2 py-0.5 bg-black/70 backdrop-blur text-[#00A86B] uppercase tracking-widest">Cat {p.category}</span>
                {p.is_international && <span className="text-[10px] px-2 py-0.5 bg-amber-500 text-black uppercase tracking-widest font-bold">Intl</span>}
                {!p.registration_fee_paid && <span className="text-[10px] px-2 py-0.5 bg-red-600 text-white uppercase tracking-widest">Fee Unpaid</span>}
                {p.sold && <span className="text-[10px] px-2 py-0.5 bg-emerald-500 text-black uppercase tracking-widest font-bold">SOLD</span>}
              </div>
            </div>
            <div className="p-3">
              <div className="font-anton text-lg uppercase text-white truncate">{p.name}</div>
              <div className="text-xs text-zinc-500">{p.country} · {p.rating}★</div>
              {p.sold && <div className="text-xs text-emerald-400 mt-1">{fmtKES(p.sold_amount)}</div>}
              {canManage ? (
                <div className="mt-3 flex gap-1">
                  <Button data-testid={`btn-view-${p.id}`} size="sm" variant="outline" onClick={() => nav(`/players/${p.id}`)} className="flex-1 border-white/10 bg-[#060d09] text-white"><Eye className="w-3 h-3" /></Button>
                  <Button data-testid={`btn-edit-${p.id}`} size="sm" variant="outline" onClick={() => startEdit(p)} className="flex-1 border-white/10 bg-[#060d09] text-white"><Pencil className="w-3 h-3" /></Button>
                  <label className="flex-1">
                    <input data-testid={`input-upload-${p.id}`} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && upload(p.id, e.target.files[0])} />
                    <span className="cursor-pointer flex items-center justify-center h-8 rounded-md border border-white/10 bg-[#060d09] text-white text-xs hover:bg-zinc-800"><Upload className="w-3 h-3" /></span>
                  </label>
                  <Button data-testid={`btn-del-${p.id}`} size="sm" variant="outline" onClick={() => del(p.id)} className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10"><Trash2 className="w-3 h-3" /></Button>
                </div>
              ) : (
                <Button data-testid={`btn-view-${p.id}`} size="sm" variant="outline" onClick={() => nav(`/players/${p.id}`)} className="mt-3 w-full border-white/10 bg-[#060d09] text-white"><Eye className="w-3 h-3 mr-1.5" /> View Profile</Button>
              )}
            </div>
          </Card>
        ))}
        {players.length === 0 && (
          <div className="col-span-full text-center text-zinc-500 py-12" data-testid="players-empty">No players yet. {canManage && "Click 'Add Player' to seed your pool."}</div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
